import { createContext, useContext, useState, useEffect } from 'react'

/**
 * Global language state. Previously each page had its own useState('zh'),
 * so switching to English on Landing would reset to Chinese on Onboarding.
 * Persists selection to localStorage so refresh + cross-tab also sync.
 */

export const SUPPORTED_LANGS = [
  { id: 'zh', label: 'zh' },
  { id: 'en', label: 'EN' },
  { id: 'ja', label: 'JP' },
]

const STORAGE_KEY = 'quanturing.lang'

const LanguageContext = createContext({
  lang: 'zh',
  setLang: () => {},
})

function detectInitialLang() {
  // ?lang= wins over everything else (SS03): a reader arriving from the English
  // /quanbrand/ page carries ?lang=en, and that explicit intent must outrank
  // whatever this browser happened to remember from an earlier visit.
  //
  // Widget.jsx already read ?lang= in an effect. Doing it here as well is not a
  // second mechanism -- it is the same decision made one render earlier, which
  // also removes the flash of the wrong language on /qt/embed.
  try {
    const q = new URLSearchParams(window.location.search).get('lang')
    if (q && SUPPORTED_LANGS.some((l) => l.id === q)) return q
    // An unsupported value (?lang=zz) deliberately falls through to the rules
    // below rather than blanking the page. "Unknown" is not "none".
  } catch {
    /* no window / malformed query */
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && SUPPORTED_LANGS.some((l) => l.id === stored)) return stored
  } catch {
    /* localStorage may be disabled */
  }
  // Fall back to browser preference if we support it
  const nav = (typeof navigator !== 'undefined' && navigator.language) || ''
  if (nav.startsWith('ja')) return 'ja'
  if (nav.startsWith('en')) return 'en'
  return 'zh'
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang)

  const setLang = (next) => {
    setLangState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }

  // Keep <html lang> in step. The entry HTML files are all hard-coded to
  // zh-TW; without this an English reader gets a page that still declares
  // itself Chinese to screen readers, translators and crawlers.
  useEffect(() => {
    const el = document.documentElement
    el.setAttribute('lang', lang === 'zh' ? 'zh-TW' : lang)
  }, [lang])

  // Sync changes from other tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && e.newValue !== lang) {
        setLangState(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [lang])

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLang() {
  return useContext(LanguageContext)
}
